// Flow 6: ein Problem melden (Meldung) in 3 Schritten — gleicher Rhythmus wie der Auftrags-Wizard,
// damit ein Nutzer, der den einen kennt, auch den anderen beherrscht. Die Foto-Erfassung ist ein
// sichtbarer Platzhalter (Portal-eigene Refs, spaeter).
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/NewNotificationWizard.jsx). Geaendert
// wurden NUR Styling-Idiom (v4-Token -> isDarkMode-Verzweigung), Theme-/i18n-Hook, i18n-Keys
// (pm.-Praefix) und die Import-/API-Pfade.
//
// MONOLITH-API-KONVENTION (wie NewOrderWizard.js): `user` ist das ERSTE Argument (traegt das JWT),
// die Funktion liefert den Body DIREKT (res?.ok). `user` wird zusaetzlich zu `me` durchgereicht und
// auch an SearchBar weitergegeben.
import React, { useRef, useState, useEffect } from 'react';
import { ArrowLeft, Camera, Check, CheckCircle2, Bell } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { createNotification, getNotifCatalogs, uploadNotificationPhoto, PHOTO_MAX_BYTES } from '../api';
import { PRIORITIES, ORDER_STEP_KEYS, SAP_SHORT_TEXT_MAX, SAP_LONG_TEXT_MAX } from '../constants';
import { Button, Card, Row, StepDots } from './ui';
import PlantSelect from './PlantSelect';
import RecentEquipmentList from './RecentEquipmentList';
import SapPayloadPanel from './SapPayloadPanel';
import SearchBar from './SearchBar';
import VoiceInput from './VoiceInput';

function NewNotificationWizard({ me, user, seedEquipment, onPlantChange, onDone, onCancel }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const plant = me.activePlant;
  const [equipment, setEquipment] = useState(seedEquipment || null);
  const [step, setStep] = useState(seedEquipment ? 2 : 1);
  const [problem, setProblem] = useState('');
  const [longText, setLongText] = useState('');
  const [priority, setPriority] = useState('3-mittel');
  // Damage/cause codes (optional) — captured at the source so the Pareto report has data.
  const [catalogs, setCatalogs] = useState([]);
  const [damage, setDamage] = useState('');
  const [cause, setCause] = useState('');
  // Photo staged locally; uploaded AFTER the create succeeded (needs the notification number).
  const [photo, setPhoto] = useState(null);
  // The photo can fail on its own while the report succeeds — that must be visible, otherwise the
  // full-screen success card claims "problem + photo" for a photo that does not exist (audit 05).
  const [photoError, setPhotoError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    getNotifCatalogs(user, 'M2', me.profile.id)
      .then((res) => { if (alive) setCatalogs(res.codes || []); })
      .catch(() => { /* dropdowns simply stay empty */ });
    return () => { alive = false; };
  }, [user, me.profile.id]);
  // Ein Idempotency-Key pro Wizard-Session: ein Retry darf nie ein Duplikat anlegen.
  const idemKey = useRef(crypto.randomUUID());

  const submit = async () => {
    setSubmitting(true);
    const [dGrp, dCode] = damage ? damage.split('|') : [];
    const [cGrp, cCode] = cause ? cause.split('|') : [];
    const body = {
      type: 'M2', plant, equipmentId: equipment.equipmentId,
      description: problem.trim(), priority,
      longText: longText.trim() || undefined,
      ...(dGrp ? { damageGroup: dGrp, damageCode: dCode } : {}),
      ...(cGrp ? { causeGroup: cGrp, causeCode: cCode } : {}),
    };
    // Monolith-API: `user` zuerst, Antwort ist direkt der JSON-Body. apiCall wirft bei !ok, daher
    // try/catch — ein Transport-/HTTP-Fehler zeigt die Fehlerzeile statt submitting=true zu haengen.
    try {
      const data = await createNotification(user, body, idemKey.current, me.profile.id);
      // Business-Failure -> neuer Key (Payload aendert sich vor dem Retry vermutlich); bei
      // geworfenen Transportfehlern bleibt der Key fuer den idempotenten Retry stehen.
      if (data && data.ok === false) idemKey.current = crypto.randomUUID();
      // Photo upload rides AFTER the create (needs the number); a failed upload never fails
      // the report — the photo can be re-added from the notification detail.
      if (data && data.ok && data.notificationId && photo) {
        try {
          await uploadNotificationPhoto(user, data.notificationId, photo, plant);
        } catch (err) {
          setPhotoError(err && err.tooLarge ? t('pm.photos.tooLarge') : t('pm.photos.uploadFailed'));
        }
      }
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: e?.message || 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.ok) {
    return (
      <div className="text-center py-10 space-y-4">
        <CheckCircle2 className={`mx-auto ${isDarkMode ? 'text-emerald-300' : 'text-emerald-500'}`} size={64} />
        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.notif.successTitle')}</h2>
        <p className={isDarkMode ? 'text-white/60' : 'text-slate-500'}>{t('pm.notif.notifNumber')}</p>
        <p className={`text-3xl font-mono font-bold ${isDarkMode ? 'text-blue-200' : 'text-brand'}`}>{result.notificationId}</p>
        {result.idempotent && (
          <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{t('pm.order.duplicateNote')}</p>
        )}
        {/* Footnote, not headline: the report IS in SAP — only the photo is missing. */}
        {photoError && (
          <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{photoError}</p>
        )}
        <div className="pt-4"><Button onClick={onDone}>{t('pm.common.done')}</Button></div>
      </div>
    );
  }

  return (
    <div>
      {/* Kopfzeile: Zurueck links, Werk rechts — die Meldung wird im AKTIVEN Werk angelegt. */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <button
          onClick={step === 1 ? onCancel : () => setStep(step - 1)}
          className={`flex items-center gap-1 font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
        >
          <ArrowLeft size={18} /> {t('pm.common.back')}
        </button>
        <PlantSelect me={me} onPlantChange={onPlantChange} />
      </div>
      <StepDots step={step} total={3} labels={ORDER_STEP_KEYS.map((k) => t(k))} />

      {step === 1 && (
        <section className="space-y-3">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.order.whichDevice')}</h2>
          <SearchBar user={user} role={me.profile.id} autoFocus onSelect={(eq) => { setEquipment(eq); setStep(2); }} />
          {/* Zuletzt verwendete Geraete des Users (bis 10, aktives Werk) — ein Tipp statt Suche. */}
          <RecentEquipmentList user={user} me={me} max={10} onSelect={(eq) => { setEquipment(eq); setStep(2); }} />
        </section>
      )}

      {step === 2 && equipment && (
        <section className="space-y-5">
          {/* Equipment-Banner — soft brand-getoent mit Bell-Badge. */}
          <Card className={`flex items-center gap-3 ${isDarkMode ? 'bg-brand/[0.08]' : 'bg-brand/[0.08]'}`}>
            <div className={`rounded-xl p-2 ${isDarkMode ? 'bg-brand/20 text-blue-200' : 'bg-brand text-white'}`}><Bell size={20} /></div>
            <div>
              <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{equipment.description}</div>
              <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                {equipment.equipmentId} · {equipment.functionalLocation}
              </div>
            </div>
          </Card>
          <div>
            <label className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.order.problemLabel')}</label>
            <div className="mt-2">
              {/* Kurztext (SAP QMTXT, 40 Zeichen) — OHNE Mikro: Diktat gehoert in die Details. */}
              <textarea
                value={problem} onChange={(e) => setProblem(e.target.value)} rows={2}
                maxLength={SAP_SHORT_TEXT_MAX}
                placeholder={t('pm.order.problemPlaceholder')}
                className={`w-full outline-none p-4 text-lg rounded-xl border transition-all duration-200 ${isDarkMode
                  ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12] placeholder:text-white/40'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40 placeholder:text-slate-400'
                }`}
              />
            </div>
            <p className={`mt-1 text-right text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
              {problem.length}/{SAP_SHORT_TEXT_MAX}
            </p>
            <label className={`mt-3 block font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.order.longTextLabel')}
            </label>
            <div className="mt-2 relative">
              {/* Langtext (Kontraktfeld longText) — HIER landet das Diktat, ohne Kappung; leerer
                  Kurztext wird aus dem ersten Diktat vorbelegt (kuerzbar). */}
              <textarea
                value={longText} onChange={(e) => setLongText(e.target.value)} rows={4}
                maxLength={SAP_LONG_TEXT_MAX}
                placeholder={t('pm.order.longTextPlaceholder')}
                className={`w-full outline-none p-4 rounded-xl border transition-all duration-200 ${isDarkMode
                  ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12] placeholder:text-white/40'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40 placeholder:text-slate-400'
                }`}
              />
              <VoiceInput
                user={user} role={me.profile.id} features={me.features}
                onText={(text) => {
                  setLongText((lt) => `${lt ? `${lt} ` : ''}${text}`.slice(0, SAP_LONG_TEXT_MAX));
                  setProblem((p) => p || text.slice(0, SAP_SHORT_TEXT_MAX));
                }}
              />
            </div>
          </div>
          <label
            className={`w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed min-h-[56px] text-sm cursor-pointer ${photo
              ? (isDarkMode ? 'border-emerald-400/40 text-emerald-300' : 'border-emerald-300 text-emerald-700')
              : (isDarkMode ? 'border-white/15 text-white/50' : 'border-slate-200 text-slate-400')
            }`}
          >
            <Camera size={18} /> {photo ? `${t('pm.photos.attached')}: ${photo.name}` : t('pm.photos.take')}
            <input type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => {
                const f = (e.target.files && e.target.files[0]) || null;
                // Size is checked HERE, not at upload time: the upload only runs after the create
                // succeeded, so the 413 would reach the technician at the success screen — long
                // after leaving the machine (audit finding 05).
                if (f && f.size > PHOTO_MAX_BYTES) { setPhoto(null); setPhotoError(t('pm.photos.tooLarge')); return; }
                setPhotoError(null);
                setPhoto(f);
              }} />
          </label>
          {photoError && (
            <p className={`text-sm ${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>{photoError}</p>
          )}
          {/* Damage/cause dropdowns (optional, SAP catalogs). Value = "group|code". */}
          {catalogs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select value={damage} onChange={(e) => setDamage(e.target.value)}
                className={`rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-800'}`}>
                <option value="">{t('pm.codes.damageNone')}</option>
                {catalogs.filter((c) => c.kind === 'damage').map((c) => (
                  <option key={`${c.group}|${c.code}`} value={`${c.group}|${c.code}`}>
                    {c.text || `${c.group}/${c.code}`}
                  </option>
                ))}
              </select>
              <select value={cause} onChange={(e) => setCause(e.target.value)}
                className={`rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-800'}`}>
                <option value="">{t('pm.codes.causeNone')}</option>
                {catalogs.filter((c) => c.kind === 'cause').map((c) => (
                  <option key={`${c.group}|${c.code}`} value={`${c.group}|${c.code}`}>
                    {c.text || `${c.group}/${c.code}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.order.priority')}</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {PRIORITIES.map((p) => {
                const active = priority === p.key;
                const cls = active
                  ? (isDarkMode ? 'bg-brand/20 text-blue-200 border-brand/40' : 'bg-brand text-white border-brand')
                  : (isDarkMode ? 'bg-white/[0.08] text-white/80 border-white/15' : 'bg-slate-50 text-slate-700 border-slate-200');
                return (
                  <button
                    key={p.key} onClick={() => setPriority(p.key)}
                    className={`min-h-[52px] rounded-xl font-semibold border-2 transition-all duration-200 ${cls}`}
                  >
                    {t(p.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={() => setStep(3)} disabled={!problem.trim()}>{t('pm.common.next')}</Button>
        </section>
      )}

      {step === 3 && equipment && (
        <section className="space-y-4">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.notif.confirmTitle')}</h2>
          <Card className="space-y-2">
            <Row label={t('pm.order.fieldDevice')} value={`${equipment.description} (${equipment.equipmentId})`} info={t('pm.info.equipment')} />
            <Row label={t('pm.order.fieldProblem')} value={problem} info={t('pm.info.problem')} />
            {longText.trim() && (
              <Row label={t('pm.order.fieldLongText')} value={longText.length > 80 ? `${longText.slice(0, 80)}…` : longText} />
            )}
            <Row label={t('pm.order.fieldPriority')} value={t(PRIORITIES.find((p) => p.key === priority)?.labelKey)} info={t('pm.info.priority')} />
          </Card>
          {/* SAP-Transparenz: exakt der Meldungs-Payload, der an SAP geht. */}
          <SapPayloadPanel
            showLabel={t('pm.sap.show')}
            hideLabel={t('pm.sap.hide')}
            note={t('pm.sap.toSapNote')}
            rows={[
              { label: t('pm.notif.fieldType'), value: 'M2' },
              { label: t('pm.order.fieldPlant'), value: plant },
              { label: t('pm.order.fieldDevice'), value: equipment.equipmentId },
              { label: t('pm.order.fieldPriority'), value: priority },
            ]}
          />
          {result && !result.ok && (
            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.order.errorPrefix')}: {result.error}</p>
          )}
          <Button variant="confirm" onClick={submit} disabled={submitting}>
            {submitting ? t('pm.notif.creating') : (<><Check /> {t('pm.notif.createBtn')}</>)}
          </Button>
          <p className={`text-center text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.notif.createHint')}</p>
        </section>
      )}
    </div>
  );
}

export default NewNotificationWizard;
